import { useState } from 'react';

import { Button } from '../../shared/ui/button';
import { DangerAction } from '../../shared/ui/danger-zone';
import { FieldLabel } from '../../shared/ui/field-label';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import { isRevoked } from '../../entities/api-key';
import type { TApiKeys } from './use-api-keys';

const PHRASE = 'revoke all';

export const RevokeAllKeys = ({ keys }: { keys: TApiKeys }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const live = (keys.keys.data ?? []).filter((key) => !isRevoked(key)).length;
  const matches = typed.trim().toLowerCase() === PHRASE;
  const count = `${live} ${live === 1 ? 'key' : 'keys'}`;

  const close = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTyped('');
      keys.revokeAll.reset();
    }
  };

  return (
    <>
      <DangerAction
        title="Revoke every key"
        blocked={keys.keys.isSuccess && live === 0 ? 'No key is active right now.' : null}
        action={
          <Button
            size="sm"
            variant="secondary"
            destructive
            disabled={live === 0}
            onClick={() => setOpen(true)}
          >
            Revoke all keys
          </Button>
        }
      >
        For when a key may have leaked and you don’t know which. Everything that calls the API with
        one stops working at once, until you give it a new key.
      </DangerAction>

      <Modal
        open={open}
        onOpenChange={close}
        title={`Revoke ${count}`}
        description={`Every script and service using ${live === 1 ? 'it' : 'them'} is refused from the next call. This can’t be undone.`}
        submit={{
          label: `Revoke ${count}`,
          onSubmit: () => keys.revokeAll.mutate(undefined, { onSuccess: () => close(false) }),
          disabled: !matches,
          waiting: keys.revokeAll.isPending,
          waitingLabel: 'Revoking',
          destructive: true,
        }}
      >
        <div className="flex flex-col gap-3 pt-2">
          <div>
            <FieldLabel htmlFor="revoke-all-confirm">Type “{PHRASE}” to confirm</FieldLabel>
            <Input
              id="revoke-all-confirm"
              mono
              autoFocus
              autoComplete="off"
              spellCheck={false}
              value={typed}
              placeholder={PHRASE}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
          <MutationError error={keys.revokeAll.error} />
        </div>
      </Modal>
    </>
  );
};
